// "Change role" from the support banner (CR_20260924 Part C, decision F): the same visit acts as another role / reach.
// The switch refreshes the token and drops every cached screen, as starting a visit does.
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { actIsValid, actOf, type SupportAct } from '@fleetspoke/core/support';
import type { SupportInfo } from '@fleetspoke/core/types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../auth/session';
import { SupportActFields } from './SupportActFields';

export function SwitchSupportRoleDialog({
  support,
  onClose,
}: {
  support: SupportInfo;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const { setSupportRole } = useSession();
  const [act, setAct] = useState<SupportAct>(() => actOf(support));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await setSupportRole(act);
      onClose();
    } catch (err) {
      const code = (err as { code?: string }).code;
      setError(t(code === '22023' ? 'support.err_places' : 'common.error_generic'));
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('support.switch_title')}</DialogTitle>
      <DialogContent dividers>
        <SupportActFields
          companyId={support.company_id}
          value={act}
          onChange={setAct}
          disabled={busy}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void submit()}
          disabled={busy || !actIsValid(act)}
          data-testid="support-switch-submit"
        >
          {t('support.switch')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
